import type { ReactNode } from "react"
import { Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles()((theme) => ({
    group: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[100]}`,
        "&:first-of-type": {
            borderTop: "none",
        },
        // On paper the sheet around the groups carries the outer spacing, repeated on every page. A divider
        // would also be drawn above a group that starts a new page, so groups are separated by spacing only.
        "@media print": {
            paddingBlock: ".75rem",
            paddingInline: 0,
            borderTop: "none",
            "&:first-of-type": {
                paddingTop: 0,
            },
            "&:last-of-type": {
                paddingBottom: 0,
            },
        },
    },
    groupHighlighted: {
        background: theme.palette.tailwind.emerald[50],
    },
    header: {
        width: "12rem",
        flexShrink: 0,
    },
    label: {
        fontWeight: 700,
    },
    labelHighlighted: {
        color: theme.palette.tailwind.emerald[700],
    },
    items: {
        flex: 1,
        minWidth: 0,
        "@media print": {
            "& > *": {
                breakInside: "avoid",
            },
        },
    },
}))

type Props = Readonly<{
    label: ReactNode
    meta: ReactNode
    isHighlighted?: boolean
    emptyText: ReactNode
    children: ReactNode
    isEmpty: boolean
}>

export function MeetingRoomsGroup({ label, meta, isHighlighted = false, emptyText, children, isEmpty }: Props) {
    const { classes, cx } = useStyles()

    return (
        <Stack
            direction="row"
            spacing="1.5rem"
            className={cx(classes.group, isHighlighted && classes.groupHighlighted)}
        >
            <Stack spacing=".25rem" className={classes.header}>
                <Typography variant="body2" className={cx(classes.label, isHighlighted && classes.labelHighlighted)}>
                    {label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {meta}
                </Typography>
            </Stack>
            <Stack spacing=".5rem" justifyContent={isEmpty ? "center" : "flex-start"} className={classes.items}>
                {isEmpty ? (
                    <Typography variant="body2" color="text.secondary">
                        {emptyText}
                    </Typography>
                ) : (
                    children
                )}
            </Stack>
        </Stack>
    )
}
